import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { EmptyState } from "../../ui/empty-state";
import { SelectField, TextField } from "../../ui/fields";
import { BackLink, TableLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { useCatalogCapabilities } from "../access";
import { ItemStatusBadge } from "../components/ItemStatusBadge";
import { filterPanel, hintText, segmentLink, segmentTrack } from "../components/styles";
import { describeCatalogError } from "../errors";
import { formatDate, itemTypeOptions } from "../labels";
import { reviewLocationOptions, reviewRefreshOptions, reviewStateOptions } from "./labels";
import { PlaceBadge } from "./parts";
import { usePlaceReview } from "./queries";
import {
  activeReviewFilterCount,
  emptyReviewFilters,
  type ReviewFilterValues,
  reviewParamsFromValues,
  reviewQueryFromParams,
  reviewValuesFromQuery,
} from "./review-filters";
import { assessmentBadge, matchBadgeOf, rowRefreshBadge } from "./state";

const typeLabel = (value: string) =>
  itemTypeOptions.find((option) => option.value === value)?.label ?? value;

type ReviewRow = NonNullable<
  ReturnType<typeof usePlaceReview>["data"]
>["pages"][number]["items"][number];

const column = createColumnHelper<ReviewRow>();

const columns = [
  column.accessor("name", {
    header: "Item",
    meta: { rowHeader: true },
    cell: (info) => (
      <TableLink
        to={`/catalog/items/${encodeURIComponent(info.row.original.itemId)}`}
        className="font-serif text-lg font-medium text-foreground"
      >
        {info.getValue()}
      </TableLink>
    ),
  }),
  column.accessor((row) => typeLabel(row.itemType), { id: "type", header: "Type" }),
  column.accessor("status", {
    header: "Status",
    cell: (info) => <ItemStatusBadge status={info.getValue()} />,
  }),
  column.display({
    id: "placeState",
    header: "Place state",
    cell: (info) => <PlaceBadge badge={matchBadgeOf(info.row.original.matchState)} />,
  }),
  column.display({
    id: "check",
    header: "Check",
    cell: (info) => {
      const row = info.row.original;
      const refresh = rowRefreshBadge(
        row.matchState,
        row.refreshState,
        row.lastErrorCode,
        row.lastVerifiedAt,
      );
      return refresh === null ? (
        <span className="text-muted-foreground">Not applicable</span>
      ) : (
        <PlaceBadge badge={refresh} />
      );
    },
  }),
  column.display({
    id: "coordinates",
    header: "Coordinates",
    cell: (info) => <PlaceBadge badge={assessmentBadge(info.row.original.locationPoint)} />,
  }),
  column.accessor((row) => [row.locality, row.countryCode].filter(Boolean).join(", "), {
    id: "locality",
    header: "Locality",
    cell: (info) => info.getValue() || "Not set",
    meta: { className: "[overflow-wrap:anywhere]" },
  }),
  column.accessor((row) => formatDate(row.updatedAt), {
    id: "updated",
    header: "Updated",
    meta: { className: "whitespace-nowrap" },
  }),
];

/**
 * The unresolved matches: items whose place is ambiguous, not found, needs a new look, or whose check
 * failed, and items whose location nobody confirmed. Any role that can read the catalog sees the list;
 * the API decides every request again. Filters live in the address. The list is a keyset page: "Load more".
 */
export function PlaceReviewPage() {
  useWideLayout();
  const [params, setParams] = useSearchParams();
  const parsed = useMemo(() => reviewQueryFromParams(params), [params]);
  const values = useMemo(() => reviewValuesFromQuery(parsed.query), [parsed.query]);
  const { capabilities, isLoading: rolesLoading } = useCatalogCapabilities();
  const list = usePlaceReview(parsed.query, capabilities.read);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="The catalog is not available for your role"
        message="Your staff role does not include catalog access."
      />
    );
  }

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const filterCount = activeReviewFilterCount(parsed.query);
  const tabParams = (state: string) => {
    const next = reviewParamsFromValues({ ...values, state });
    return next.toString() === "" ? "" : `?${next.toString()}`;
  };

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title="Places to review"
        description={`Items where the Google place is not settled, and items whose location nobody confirmed.${capabilities.edit ? "" : " You have read-only access."}`}
        breadcrumbs={<BackLink to="/catalog">Back to the catalog</BackLink>}
      />

      {parsed.ignored.length > 0 ? (
        <Notice tone="warning" role="status">
          Some filters in the address were not valid and were ignored: {parsed.ignored.join(", ")}.
        </Notice>
      ) : null}

      <nav aria-label="Show items by place state">
        <ul className={segmentTrack}>
          <li>
            <Link
              className={segmentLink}
              to={{ search: tabParams("") }}
              aria-current={values.state === "" ? "page" : undefined}
            >
              All that need a person
            </Link>
          </li>
          {reviewStateOptions.map((option) => (
            <li key={option.value}>
              <Link
                className={segmentLink}
                to={{ search: tabParams(option.value) }}
                aria-current={values.state === option.value ? "page" : undefined}
              >
                {option.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <ReviewFilters
        key={params.toString()}
        initial={values}
        onApply={(next) => setParams(reviewParamsFromValues(next))}
      />

      <div aria-busy={list.isFetching} className="grid min-w-0 gap-4">
        {list.isPending ? (
          <AsyncState kind="loading" title="Loading the list" message="Reading the first page." />
        ) : list.isError ? (
          <AsyncState
            kind="error"
            title="The list could not be loaded"
            message={describeCatalogError(list.error).message}
            onRetry={() => void list.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            className="rounded-lg border border-hairline bg-card"
            title="Nothing to review"
            description={
              filterCount > 0
                ? "No item matches these filters. Clear the filters to see everything."
                : "Every item has a settled place."
            }
          />
        ) : (
          <>
            <DataTable
              banded
              columns={columns}
              data={rows}
              getRowId={(row) => row.itemId}
              caption={`Items to review${
                filterCount > 0
                  ? ` (${filterCount} ${filterCount === 1 ? "filter" : "filters"} applied)`
                  : ""
              }`}
            />
            <div className="flex flex-wrap items-center gap-4">
              <p role="status" className={hintText}>
                Showing {rows.length} {rows.length === 1 ? "item" : "items"}
                {list.hasNextPage ? ". More are available." : ". That is all of them."}
              </p>
              {list.hasNextPage ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReviewFilters({
  initial,
  onApply,
}: Readonly<{ initial: ReviewFilterValues; onApply: (values: ReviewFilterValues) => void }>) {
  const [values, setValues] = useState(initial);
  const set = (key: keyof ReviewFilterValues) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);
  const anySet = Object.values(initial).some((value) => value !== "");
  return (
    <form
      className={cn(filterPanel, "md:flex md:items-start")}
      aria-label="Filter the list"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onApply(values);
      }}
    >
      <div className="grid flex-1 gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,11.25rem),1fr))]">
        <SelectField
          id="review-refresh"
          label="Check"
          value={values.refresh}
          onChange={set("refresh")}
          options={reviewRefreshOptions}
          placeholder="Any"
        />
        <SelectField
          id="review-location"
          label="Location"
          value={values.location}
          onChange={set("location")}
          options={reviewLocationOptions}
          placeholder="Any"
        />
        <SelectField
          id="review-type"
          label="Type"
          value={values.type}
          onChange={set("type")}
          options={itemTypeOptions}
          placeholder="Any"
        />
        <TextField
          id="review-country"
          label="Country code"
          value={values.countryCode}
          onChange={set("countryCode")}
          maxLength={2}
          hint="Two letters, for example ES."
        />
      </div>
      <div className="flex flex-wrap gap-2.5 md:mt-6">
        <Button type="submit" disabled={!dirty}>
          Apply filters
        </Button>
        {anySet ? (
          <Button type="button" variant="secondary" onClick={() => onApply(emptyReviewFilters)}>
            Clear filters
          </Button>
        ) : null}
      </div>
    </form>
  );
}
